import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Checkbox, Fab, Grid, Paper, TextField, Tooltip, Typography } from "@mui/material";
import { Delete, Info, Search } from "@mui/icons-material";
import { Field, Form, Formik } from "formik";
import { toast } from "react-toastify";

import { translate } from "../../../translate/translate";
import TablePaginationComponent, { Order } from "../../../components/table/table-pagination.component";
import DialogVersion from "../../../components/dialog/dialog.component";
import WarningTooltip from "../../../components/tooltip/warning-tooltip";
import Loading from "../../../components/loading/loading";
import { ErrorComponent } from "../../../components/error/error.component";

import { BaseService } from "../../../services/base-service";
import { URLS } from "../../../services/app-urls";

import { PaginatedResult } from "../../../models/default/paginated-result";
import { Bom } from "../../../models/general/bom";
import { Group } from "../../../models/account/group";


type BulkDeleteResponse = {
  deleted: number[];
  errors: Array<{ id: number; reason: string }>;
  message?: string;
};

const orderingMap: { [column: string]: string } = {
  bom_model: "bom_number",
  description: "final_product__description",
  imported_at: "imported_at",

};

const BOMComponent = () => {
  const service = new BaseService<Bom>(URLS.BOM);
  const [dataSource, setDataSource] = useState<Bom[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchBOM, setSearchBOM] = useState("");
  const [searchPartNumber, setSearchPartNumber] = useState("");
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState("imported_at");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const navigate = useNavigate();
  const url = "/general/bom/";

  const search = () => {
    setIsLoading(true);

    service.clearParameters();
    const sanitizedBOM = searchBOM.trim();
    const sanitizedPartNumber = searchPartNumber.trim();

    if (sanitizedBOM) service.addParameter("bom", sanitizedBOM);
    if (sanitizedPartNumber) service.addParameter("part_number", sanitizedPartNumber);
    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    const orderingField = orderingMap[orderBy] || orderBy;
    service.addParameter("ordering", order === "asc" ? orderingField : `-${orderingField}`);

    service
      .getAllPaginate()
      .then((response) => {
        setPagination(response.data);
        setDataSource(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    search();
  }, [page, rowsPerPage, order, orderBy]);

  const mappedDataSource = useMemo(() => {
    return dataSource.map(bom => ({
      ...bom,
      description: bom.final_product?.description || "N/A",
    }));
  }, [dataSource]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setIsLoading(true);

  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);

  };

  const handleToggleStatus = (row: any) => {
    service
      .update({ active: !row.active }, row.id)
      .then(() => {
        toast.success(translate.t("status_updated"));
        search();
      })
      .catch((error: any) => {
        ErrorComponent(error);
      });
  };

  const handleDoubleClick = (row: Group) => {
    setIsLoading(true);
    navigate(`${url}${row.id}`, { state: { fromBOM: true } });
  };

  const handleIconBomEmpty = (hasBomEmpty: boolean) => {
    return hasBomEmpty && <WarningTooltip messageKey={"bom_not_imported"} />;
  };

  const toggleSelectAll = useCallback(() => {
    const selectableIds = mappedDataSource
      .filter(d => !d.has_active_analysis)
      .map(d => d.id);

    if (selectedIds.length === selectableIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableIds);
    }
  }, [selectedIds, mappedDataSource]);

  const toggleSelectOne = useCallback((id: number) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id],
    );
  }, []);

  const confirmDeleteMessage = useMemo(() => {
    if (selectedIds.length > 1) {
      return translate.t("confirm_delete_itens");
    }
    return translate.t("confirm_delete");
  }, [selectedIds]);

  const handleOpenDeleteModal = useCallback(() => {
    setOpenDeleteModal(true);
  }, []);

  const handleBulkDeleteToast = useCallback((
    data: BulkDeleteResponse
  ) => {
    const { deleted, errors, message } = data as unknown as BulkDeleteResponse;

    if (message) {
      if (deleted.length > 0) {
        toast.success(message);
      } else {
        toast.error(message);
      }
      return;
    }

    if (errors.length > 0) {
      const reasonGroups = new Map<string, number>();

      errors.forEach(error => {
        const baseReason = error.reason.replace(/\s*\(IDs?:\s*[\d,\s]+\)/gi, '').trim();

        const currentCount = reasonGroups.get(baseReason) || 0;
        reasonGroups.set(baseReason, currentCount + 1);
      });

      const consolidatedReasons = Array.from(reasonGroups.entries())
        .map(([reason, count]) => {
          const countText = count > 1 ? ` (${count}x)` : '';
          return `${reason}${countText}`;
        })
        .join(', ');

      if (deleted.length === 0) {
        const errorMessage = translate.t("error_deleting_boms");
        toast.error(`${errorMessage}: ${consolidatedReasons}`);
      } else {
        const warningMessage = translate.t("some_boms_not_deleted");
        toast.warning(`${warningMessage}: ${consolidatedReasons}`);
      }
    } else if (deleted.length > 0) {
      toast.success(translate.t("boms_successfully_deleted"));
    }
  }, []);

  const handleCloseDeleteModal = useCallback(
    async (result: unknown) => {
      if (typeof result === "boolean" && result) {
        try {
          const response = await service.postFromListRoute({ ids: selectedIds }, "bulk-delete");
          handleBulkDeleteToast(response.data as unknown as BulkDeleteResponse);
        } catch (error: any) {
          ErrorComponent(error as any);
        } finally {
          search();
          setSelectedIds([]);
          setOpenDeleteModal(false);
        }
      } else {
        setSelectedIds([]);
        setOpenDeleteModal(false);
      }
    },
    [selectedIds, mappedDataSource, service, search, handleBulkDeleteToast]
  );

  const renderCellHandler = (column: string, index: number, row: any) => {
    if (column === "bom_number") {
      return (
        <Box display="flex" alignItems="center">
          <Typography variant="body1" id="material">
            {row.bom_number}
          </Typography>
          {handleIconBomEmpty(row.is_empty)}
        </Box>
      );
    }
    return null;
  };

  const renderCheckboxCell = useCallback(
    (row: Bom) => {
      const isDisabled = row.has_active_analysis === true;
      const checkbox = (
        <Checkbox
          checked={selectedIds.includes(row.id)}
          disabled={isDisabled}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleSelectOne(row.id)}
          color="primary"
          sx={{
            padding: 0.5,
            "& .MuiSvgIcon-root": { fontSize: 20 },
          }}
        />
      );

      return (
        <Box display="flex" sx={{ height: "100%" }}>
          {isDisabled ? (
            <Tooltip title={translate.t("cannot_delete_bom_with_active_analysis")}>
              <span>{checkbox}</span>
            </Tooltip>
          ) : (
            checkbox
          )}
        </Box>
      );
    },
    [selectedIds, toggleSelectOne]
  );

  const renderHeaderCheckbox = useCallback(
    () => (
      <Box
        display="flex"
        sx={{ height: "100%" }}
      >
        <Checkbox
          indeterminate={
            selectedIds.length > 0 && selectedIds.length < dataSource.length
          }
          checked={
            selectedIds.length === dataSource.length && dataSource.length > 0
          }
          onChange={toggleSelectAll}
          sx={{
            padding: 0.5,
            "& .MuiSvgIcon-root": { fontSize: 20 },
            color: "white",
            "& .MuiSvgIcon-root path": {
              stroke: "white",
            },
            "&.Mui-checked": {
              color: "white",
            },
            "&.MuiCheckbox-indeterminate": {
              color: "white",
            },
            "& .MuiTouchRipple-root": {
              color: "white",
            },
          }}
        />
      </Box>
    ),
    [selectedIds, dataSource, toggleSelectAll]
  );

  const onRequestSort = (column: string) => {
    const isAsc = orderBy === column && order === "asc";
    const isDesc = orderBy === column && order === "desc";

    if (isAsc) {
      setOrder("desc");
      setOrderBy(column);
    } else if (isDesc) {
      setOrder("asc");
      setOrderBy("");
    } else {
      setOrder("asc");
      setOrderBy(column);
    }
  };

  const renderSelectedBoms = () => {
    if (selectedIds.length === 0) return null;

    return (
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "10px",
          border: "1px solid #E0E0E0",
          backgroundColor: "#FAFAFA",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 300,
          minWidth: 500,
          overflowY: "overlay",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {mappedDataSource.map((bom, idx) => {
          if (!selectedIds.includes(bom.id)) return null;

          return (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                padding: "8px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: "40px",
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: "14px",
                  color: "#212121",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  paddingLeft: "12px",
                }}
              >
                {bom.bom_number}
              </Typography>
            </Paper>
          );
        })}
      </Paper>
    );
  };

  const renderSelectionBar = () => {
    if (selectedIds.length === 0) return null;

    return (
      <Box
        sx={{
          mt: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#FFF8E1",
          border: "1px solid #F8B653",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "16px",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Info sx={{ color: "#FBC02D" }} />
          <Typography sx={{ color: "#6D4C41", fontWeight: 500 }}>
            {selectedIds.length}{" "}
            {selectedIds.length === 1
              ? translate.t("selected_bom")
              : translate.t("selected_boms")}
          </Typography>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          startIcon={<Delete />}
          sx={{
            backgroundColor: "#3949AB",
            "&:hover": { backgroundColor: "#1F0063" },
            textTransform: "none",
            fontWeight: 600,
          }}
          onClick={() => {
            selectedIds.forEach((id) => {
              const selected = mappedDataSource.some((item) => item.id === id);
              if (selected) handleOpenDeleteModal();
            });
          }}
        >
          {[translate.t("delete_item")]}
        </Button>
      </Box>
    );
  };

  return (
    <section>
      <div className="floating-title" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Typography variant={"h5"}>{translate.t("bom")}</Typography>
      </div>

      <Grid item xs={12}>
        <Paper variant="outlined">
          <Box p={2}>
            <Formik
              enableReinitialize={true}
              initialValues={{ bomNumber: "", partNumber: "" }}
              onSubmit={(values, { setSubmitting }) => {
                if (page === 0) {
                  search();
                } else {
                  setPage(0);
                }
                setSubmitting(false);
              }}
            >
              {() => (
                <Form autoComplete="off">
                  <Grid alignItems="center" container spacing={2} direction={"row"}>
                    <Grid item xs={12} sm={5}>
                      <Field
                        as={TextField}
                        name="bom_number"
                        label={translate.t("bom_number")}
                        placeholder="Ex: BOM1;BOM2"
                        fullWidth
                        variant={"outlined"}
                        value={searchBOM}
                        onChange={(event: ChangeEvent<{ value: unknown }>) => setSearchBOM(event.target.value as string)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        name="partNumber"
                        label={translate.t("part_number")}
                        placeholder="Ex: PART_NUMEBER1;PART_NUMBER2"
                        fullWidth
                        variant={"outlined"}
                        value={searchPartNumber}
                        onChange={(event: ChangeEvent<{ value: unknown }>) => {
                          const value = (event.target.value as string)
                            .split(";")
                            .map(s => s.trim())
                            .join(";");
                          setSearchPartNumber(value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1} display="flex" justifyContent="flex-end">
                      <Fab size="small" type="submit" color="primary">
                        <Search />
                      </Fab>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <div>
        {renderSelectionBar()}
      </div>

      <Loading open={isLoading} />

      <Paper variant="outlined" sx={{ marginTop: "16px" }}>
        <TablePaginationComponent
          state={mappedDataSource}
          displayedColumnsShow={["select", "modified_at", "bom_model", "description", "status"]}
          displayedColumns={["select", "modified_at", "bom_number", "description", "active"]}
          dateColumns={["modified_at"]}
          count={pagination?.count}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          handleSwitch={handleToggleStatus}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleDoubleClick={handleDoubleClick}
          renderCellHandler={{ bom_number: renderCellHandler, select: (_: string, __: number, row: any) => renderCheckboxCell(row), }}
          renderHeaderCheckbox={renderHeaderCheckbox}
          message={confirmDeleteMessage}
          colorButton="error"
          columnStyles={{ 1: "5%", 2: "15%", 3: "15%", 4: "50%", 5: "5%" }}
          sortingProps={{
            sortingColumns: ["imported_at", "bom_model", "description"],
            order: order,
            orderBy: orderBy,
            onRequestSort: onRequestSort,
          }}
        />
      </Paper>

      {openDeleteModal && (
        <DialogVersion
          id="dialog-delete-bom"
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          title={translate.t("exclude_bom")}
          message={confirmDeleteMessage}
          labelButton={translate.t("delete")}
          colorButton="error"
        >
          {renderSelectedBoms()}
        </DialogVersion>
      )}
    </section>
  );
};

export default BOMComponent;
